import { Link } from 'react-router-dom'
import { useLanguage } from '../contexts/LanguageContext'
import { Home, ArrowRight, Search, FileText, Briefcase, BookOpen, Users, Calculator } from 'lucide-react'
import { Helmet } from 'react-helmet-async'

const NotFound = () => {
  const { language } = useLanguage()
  
  const popularLinks = [
    { 
      icon: Home, 
      title: language === 'tr' ? 'Ana Sayfa' : language === 'en' ? 'Home' : 'Главная',
      path: '/',
      description: language === 'tr' ? 'Ana sayfaya dön' : language === 'en' ? 'Go to homepage' : 'Вернуться на главную'
    },
    { 
      icon: Briefcase, 
      title: language === 'tr' ? 'Hizmetler' : language === 'en' ? 'Services' : 'Услуги',
      path: '/services',
      description: language === 'tr' ? 'Tüm hizmetlerimizi görüntüle' : language === 'en' ? 'View all services' : 'Посмотреть все услуги'
    },
    { 
      icon: FileText, 
      title: language === 'tr' ? 'Blog' : language === 'en' ? 'Blog' : 'Блог',
      path: '/blog',
      description: language === 'tr' ? 'Son blog yazılarımız' : language === 'en' ? 'Latest blog posts' : 'Последние статьи блога'
    },
    { 
      icon: Calculator, 
      title: language === 'tr' ? 'Hesaplama Araçları' : language === 'en' ? 'Marketing Tools' : 'Инструменты',
      path: '/marketing-tools',
      description: language === 'tr' ? 'ROI ve ROAS hesaplayıcı' : language === 'en' ? 'ROI and ROAS calculator' : 'Калькулятор ROI и ROAS'
    },
    { 
      icon: BookOpen, 
      title: language === 'tr' ? 'Akademi' : language === 'en' ? 'Academy' : 'Академия',
      path: '/academy',
      description: language === 'tr' ? 'Eğitim içerikleri' : language === 'en' ? 'Educational content' : 'Образовательный контент'
    },
    { 
      icon: Users, 
      title: language === 'tr' ? 'Hakkımızda' : language === 'en' ? 'About Us' : 'О нас',
      path: '/about',
      description: language === 'tr' ? 'Bizi tanıyın' : language === 'en' ? 'Learn about us' : 'Узнайте о нас'
    }
  ]
  
  return (
    <>
      <Helmet>
        <title>{language === 'tr' ? '404 - Sayfa Bulunamadı | Thinklyze' : language === 'en' ? '404 - Page Not Found | Thinklyze' : '404 - Страница не найдена | Thinklyze'}</title>
        <meta name="description" content={language === 'tr' ? 'Aradığınız sayfa bulunamadı. Ana sayfaya dönün veya popüler sayfalarımızı ziyaret edin.' : language === 'en' ? 'The page you are looking for was not found. Return to homepage or visit our popular pages.' : 'Страница, которую вы ищете, не найдена. Вернитесь на главную или посетите наши популярные страницы.'} />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      
      <div className="pt-16 min-h-screen bg-white">
        <div className="w-[85%] max-w-4xl mx-auto px-6 py-16 sm:py-24">
          {/* 404 Header */}
          <div className="text-center mb-16">
            <h1 className="text-7xl sm:text-[8.4rem] font-bold text-gray-900 mb-6 tracking-tight">404</h1>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
          {language === 'tr' ? 'Sayfa Bulunamadı' : language === 'en' ? 'Page Not Found' : 'Страница не найдена'}
        </h2>
            <p className="text-base text-gray-600 max-w-2xl mx-auto leading-relaxed">
              {language === 'tr' 
                ? 'Aradığınız sayfa bulunamadı. Sayfa taşınmış, silinmiş veya hiç var olmamış olabilir.' 
                : language === 'en' 
                ? 'The page you are looking for was not found. It may have been moved, deleted, or never existed.' 
                : 'Страница, которую вы ищете, не найдена. Возможно, она была перемещена, удалена или никогда не существовала.'}
        </p>
          </div>

          {/* Popular Links Grid */}
          <div className="mb-12">
            <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">
              {language === 'tr' ? 'Popüler Sayfalar' : language === 'en' ? 'Popular Pages' : 'Популярные страницы'}
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {popularLinks.map((link) => {
                const Icon = link.icon
                return (
                  <Link
                    key={link.path}
                    to={link.path}
                    className="group bg-white rounded-lg p-4 border-2 border-gray-200 hover:border-gray-900 hover:shadow-lg transition-all duration-300"
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center group-hover:bg-gray-900 transition-colors">
                        <Icon className="w-5 h-5 text-gray-700 group-hover:text-white transition-colors" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm font-semibold text-gray-900 mb-1 group-hover:text-gray-900 transition-colors">
                          {link.title}
                        </h4>
                        <p className="text-xs text-gray-600 line-clamp-2">
                          {link.description}
                        </p>
                        <div className="mt-2 flex items-center text-gray-600 text-xs font-medium group-hover:text-gray-900 transition-colors">
                          {language === 'tr' ? 'Ziyaret et' : language === 'en' ? 'Visit' : 'Посетить'}
                          <ArrowRight className="w-3 h-3 ml-1" />
                        </div>
                      </div>
                    </div>
                  </Link>
                )
              })}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
        <Link
          to="/"
              className="inline-flex items-center justify-center px-8 py-2.5 bg-gray-900 text-white font-semibold rounded-lg hover:bg-gray-800 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
        >
              <Home className="w-5 h-5 mr-2" />
          {language === 'tr' ? 'Ana Sayfaya Dön' : language === 'en' ? 'Go to Homepage' : 'Вернуться на главную'}
        </Link>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center px-8 py-2 bg-white text-gray-900 font-semibold rounded-lg border-2 border-gray-900 hover:bg-gray-900 hover:text-white transition-all duration-200 shadow-sm hover:shadow-lg"
            >
              <Search className="w-5 h-5 mr-2" />
              {language === 'tr' ? 'İletişime Geç' : language === 'en' ? 'Contact Us' : 'Связаться с нами'}
            </Link>
          </div>
        </div>
      </div>
    </>
  )
}

export default NotFound

